import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';

const API_URL = process.env.REACT_APP_API_URL;

// ⏱️ QR Expiry — must match QR_EXPIRY_MINUTES in backend/routes/GenerateQrLDBAPI.js
const QR_EXPIRY_MINUTES = 10; // minutes

// Format amount as ###,###.00
const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

function ManualPayment() {
  const { getMessage } = useTranslation();

  // Form states
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleData, setVehicleData] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('qr');
  const [paymentMethodLocked, setPaymentMethodLocked] = useState(false); // true = forced QR only (no Mifare/OBU device)
  const [cardId, setCardId] = useState('');
  const [balance, setBalance] = useState(null);
  const [gateId, setGateId] = useState('BOK');
  const [notes, setNotes] = useState('');

  // UI states
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [barriers, setBarriers] = useState([]);
  const [loadingBarriers, setLoadingBarriers] = useState(true);

  // Vehicle selection for duplicate license plates
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [showVehicleSelection, setShowVehicleSelection] = useState(false);

  // Vehicle type selection for new/unfound vehicles
  const [selectedVehicleType, setSelectedVehicleType] = useState('');
  const [tariffs, setTariffs] = useState([]);
  const [showVehicleTypeSelection, setShowVehicleTypeSelection] = useState(false);

  // QR Payment states
  const [qrImage, setQrImage] = useState(null);
  const [qrTransactionData, setQrTransactionData] = useState(null);
  const [qrPaymentStatus, setQrPaymentStatus] = useState(null);
  const [checkingQrPayment, setCheckingQrPayment] = useState(false);
  const [qrExpiresAt, setQrExpiresAt] = useState(null);   // timestamp when QR expires
  const [qrTimeLeft, setQrTimeLeft] = useState(null);     // seconds remaining

  // Refs for tracking intervals and timeouts to avoid stale closures and leaks
  const qrStatusIntervalRef = useRef(null);
  const qrPollingTimeoutRef = useRef(null); // safety-net timeout for QR polling
  const barrierTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null);
  const qrExpiryTimerRef = useRef(null); // countdown timer ref

  // Load barriers/gates and tariffs on component mount
  useEffect(() => {
    loadBarriers();
    loadTariffs();
  }, []);

  // ⏳ QR Countdown Timer — ticks every second, auto-expires when time runs out
  useEffect(() => {
    if (qrExpiryTimerRef.current) {
      clearInterval(qrExpiryTimerRef.current);
      qrExpiryTimerRef.current = null;
    }

    if (!qrExpiresAt) {
      setQrTimeLeft(null);
      return;
    }

    const tick = () => {
      const secondsLeft = Math.floor((qrExpiresAt - Date.now()) / 1000);
      if (secondsLeft <= 0) {
        setQrTimeLeft(0);
        // Stop polling and mark as expired
        if (qrStatusIntervalRef.current) {
          clearInterval(qrStatusIntervalRef.current);
          qrStatusIntervalRef.current = null;
        }
        setQrPaymentStatus(prev =>
          (prev !== 'paid') ? 'expired' : prev
        );
        clearInterval(qrExpiryTimerRef.current);
        qrExpiryTimerRef.current = null;
      } else {
        setQrTimeLeft(secondsLeft);
      }
    };

    tick();
    qrExpiryTimerRef.current = setInterval(tick, 1000);
    return () => {
      if (qrExpiryTimerRef.current) clearInterval(qrExpiryTimerRef.current);
    };
  }, [qrExpiresAt]);

  // Auto-search when license plate changes
  useEffect(() => {
    // 🧹 Immediately clear old QR when plate changes — prevents confusion with stale QR
    if (qrExpiryTimerRef.current) {
      clearInterval(qrExpiryTimerRef.current);
      qrExpiryTimerRef.current = null;
    }
    if (qrStatusIntervalRef.current) {
      clearInterval(qrStatusIntervalRef.current);
      qrStatusIntervalRef.current = null;
    }
    if (qrPollingTimeoutRef.current) {
      clearTimeout(qrPollingTimeoutRef.current);
      qrPollingTimeoutRef.current = null;
    }
    setQrImage(null);
    setQrTransactionData(null);
    setQrPaymentStatus(null);
    setQrExpiresAt(null);
    setQrTimeLeft(null);

    const timer = setTimeout(() => {
      if (licensePlate && licensePlate.length >= 3) {
        searchVehicle();
      } else {
        setVehicleData(null);
        setMessage({ type: '', text: '' });
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licensePlate]);

  // Auto-calculate amount when vehicle is found
  useEffect(() => {
    if (vehicleData?.tariff) {
      setAmount(vehicleData.tariff.kip_rate);
    }
  }, [vehicleData]);

  // Load barriers/gates
  const loadBarriers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token, skipping barriers load');
        setLoadingBarriers(false);
        return;
      }

      const response = await axios.get(
        `${API_URL}/manual-payment/barriers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.data.length > 0) {
        setBarriers(response.data.data);
        // Set first barrier as default
        if (response.data.data[0].barrier_code) {
          setGateId(response.data.data[0].barrier_code);
        }
      }
    } catch (error) {
      console.error('Error loading barriers:', error);
      // Keep default gate value if load fails
    } finally {
      setLoadingBarriers(false);
    }
  };

  // Load tariffs for vehicle type selection
  const loadTariffs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/manual-payment/tariffs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.data.length > 0) {
        setTariffs(response.data.data);
        console.log('📋 Loaded tariffs:', response.data.data);
      }
    } catch (error) {
      console.error('Error loading tariffs:', error);
    }
  };

  // Search vehicle
  const searchVehicle = async () => {
    if (!licensePlate || licensePlate.length < 3) return;

    // Clear stale QR state before searching for a new vehicle
    setQrImage(null);
    setQrTransactionData(null);
    setQrPaymentStatus(null);
    setQrExpiresAt(null);
    setQrTimeLeft(null);

    setSearching(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setMessage({ type: 'error', text: 'Please login first to use Manual Payment' });
        setSearching(false);
        return;
      }

      const url = `${API_URL}/manual-payment/vehicle/${encodeURIComponent(licensePlate)}?gate=${gateId}`;
      console.log('🔍 Searching vehicle:', licensePlate, 'at gate:', gateId);
      console.log('🌐 API URL:', API_URL);
      console.log('🔗 Full URL:', url);
      console.log('🔑 Token present:', !!token);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📡 API Response:', response.data);

      if (response.data.success && response.data.data.length > 0) {
        const vehicles = response.data.data;

        // Check if multiple vehicles found
        if (vehicles.length > 1) {
          console.log('🔍 Multiple vehicles found:', vehicles.length);
          setVehicleOptions(vehicles);
          setShowVehicleSelection(true);
          setVehicleData(null);
          setMessage({
            type: 'info',
            text: `Found ${vehicles.length} vehicles with plate "${licensePlate}". Please select one.`
          });
          return;
        }

        // Single vehicle found
        const vehicle = vehicles[0];
        console.log('✅ Vehicle found:', vehicle);
        setVehicleData(vehicle);
        setShowVehicleSelection(false);
        setVehicleOptions([]);

        // Update license plate input with complete license plate from found vehicle
        setLicensePlate(vehicle.license_plate);

        setMessage({ type: 'success', text: `Vehicle ${vehicle.license_plate} found!` });

        // Auto-fill payment method based on device
        if (vehicle.mifare_card) {
          setCardId(vehicle.mifare_card);
          setPaymentMethod('card');
          setPaymentMethodLocked(false);
          checkBalance(vehicle.mifare_card, 'mifare');
        } else if (vehicle.obu_device_id) {
          setCardId(vehicle.obu_device_id);
          setPaymentMethod('obu');
          setPaymentMethodLocked(false);
          checkBalance(vehicle.obu_device_id, 'obu');
        } else {
          // No Mifare or OBU device → force QR LDB payment only
          console.log('⚠️ No Mifare/OBU device found for vehicle, forcing QR LDB payment');
          setPaymentMethod('qr');
          setPaymentMethodLocked(true);
          setCardId('');
          setBalance(null);
          // Auto-generate QR if amount is already set
          if (vehicle.tariff?.kip_rate) {
            setTimeout(() => generateQRCode(), 300);
          }
        }
      } else {
        console.log('⚠️ Vehicle not found');
        setVehicleData(null);
        setShowVehicleTypeSelection(true);  // Show vehicle type selection
        setSelectedVehicleType('');  // Reset selected type
        setAmount('');  // Reset amount until vehicle type is selected
        // Vehicle not found → no device mapping → force QR LDB
        setPaymentMethod('qr');
        setPaymentMethodLocked(true);
        setCardId('');
        setBalance(null);
        setMessage({ type: 'warning', text: 'Vehicle not found. Please select vehicle type to get the correct tariff. Payment will be via QR LDB only.' });
      }
    } catch (error) {
      console.error('Search error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Search failed. Please try again.';
      setMessage({ type: 'error', text: errorMsg });
      setVehicleData(null);
    } finally {
      setSearching(false);
    }
  };

  // Handle vehicle selection from list
  const handleVehicleSelect = (vehicle) => {
    console.log('🎯 Selected vehicle:', vehicle);
    console.log('💰 Vehicle tariff data:', vehicle.tariff);
    console.log('💵 Suggested amount:', vehicle.suggested_amount);

    setVehicleData(vehicle);
    setShowVehicleSelection(false);
    setVehicleOptions([]);

    // Update license plate input with complete license plate from selected vehicle
    setLicensePlate(vehicle.license_plate);

    setMessage({ type: 'success', text: `Vehicle ${vehicle.license_plate} selected!` });

    // Auto-fill amount from tariff
    if (vehicle.tariff && vehicle.tariff.kip_rate) {
      console.log('✅ Setting amount from tariff:', vehicle.tariff.kip_rate);
      setAmount(vehicle.tariff.kip_rate);
    } else if (vehicle.suggested_amount) {
      console.log('✅ Setting amount from suggested_amount:', vehicle.suggested_amount);
      setAmount(vehicle.suggested_amount);
    } else {
      console.warn('⚠️ No tariff data found for selected vehicle');
    }

    // Auto-fill payment method based on device
    if (vehicle.mifare_card) {
      setCardId(vehicle.mifare_card);
      setPaymentMethod('card');
      setPaymentMethodLocked(false);
      checkBalance(vehicle.mifare_card, 'mifare');
    } else if (vehicle.obu_device_id) {
      setCardId(vehicle.obu_device_id);
      setPaymentMethod('obu');
      setPaymentMethodLocked(false);
      checkBalance(vehicle.obu_device_id, 'obu');
    } else {
      // No Mifare or OBU device → force QR LDB payment only
      console.log('⚠️ No Mifare/OBU device for selected vehicle, forcing QR LDB payment');
      setPaymentMethod('qr');
      setPaymentMethodLocked(true);
      setCardId('');
      setBalance(null);
      if (vehicle.tariff?.kip_rate) {
        setTimeout(() => generateQRCode(), 300);
      }
    }

    // Hide vehicle type selection since vehicle was found
    setShowVehicleTypeSelection(false);
  };

  // Handle vehicle type selection for unfound vehicles
  const handleVehicleTypeSelect = (vehicleType) => {
    console.log('🚗 Selected vehicle type:', vehicleType);
    setSelectedVehicleType(vehicleType);

    // Find matching tariff for this vehicle type
    const matchingTariff = tariffs.find(t => t.vehicle_type === vehicleType);

    if (matchingTariff) {
      console.log('💰 Found tariff:', matchingTariff);
      setAmount(matchingTariff.kip_rate);
      // Create a virtual vehicle data with tariff for display
      setVehicleData({
        license_plate: licensePlate,
        vehicle_type: vehicleType,
        tariff: matchingTariff
      });
      setMessage({ type: 'success', text: `Vehicle type "${vehicleType}" selected. Amount: ${formatAmount(matchingTariff.kip_rate)} ₭` });
    } else {
      console.warn('⚠️ No tariff found for vehicle type:', vehicleType);
      setAmount('');
      setMessage({ type: 'error', text: `No tariff found for vehicle type "${vehicleType}". Please contact admin.` });
    }
  };

  // Check balance
  const checkBalance = async (card, deviceType) => {
    if (!card) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/manual-payment/check-balance`,
        { cardId: card, deviceType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBalance(response.data.balance);
      }
    } catch (error) {
      console.error('Balance check error:', error);
    }
  };

  // Generate QR code for payment
  const generateQRCode = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid amount first' });
      return;
    }

    if (!licensePlate) {
      setMessage({ type: 'error', text: 'Please enter license plate first' });
      return;
    }

    // 🔄 Reset ALL previous QR timers/state so countdown always starts fresh from 10:00
    if (qrExpiryTimerRef.current) {
      clearInterval(qrExpiryTimerRef.current);
      qrExpiryTimerRef.current = null;
    }
    if (qrStatusIntervalRef.current) {
      clearInterval(qrStatusIntervalRef.current);
      qrStatusIntervalRef.current = null;
    }
    if (qrPollingTimeoutRef.current) {
      clearTimeout(qrPollingTimeoutRef.current);
      qrPollingTimeoutRef.current = null;
    }
    setQrExpiresAt(null);
    setQrTimeLeft(null);
    setQrPaymentStatus(null);
    setQrImage(null);
    setQrTransactionData(null);

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ldb/generate-qr`,
        {
          amount: parseFloat(amount),
          description: `Manual Payment - ${licensePlate} - ${gateId}`,
          mobile_num: '2097285066',
          source: 'manual'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setQrImage(response.data.qrCodeImage);
        setQrTransactionData({
          transactionId: response.data.transactionId,
          reference2: response.data.reference2,
          amount: response.data.amount
        });
        setQrPaymentStatus('pending');

        // ⏱️ Set QR expiry time from backend value (QR_EXPIRY_MINUTES is the fallback)
        const expiryMin = response.data.qrExpiryMinutes || QR_EXPIRY_MINUTES;
        const expiresAt = Date.now() + expiryMin * 60 * 1000;
        setQrExpiresAt(expiresAt);
        // set time left in seconds
        setQrTimeLeft(expiryMin * 60);
        console.log(`⏱️ QR timer: ${expiryMin}:00 countdown started. Expires at ${new Date(expiresAt).toLocaleTimeString()}`);

        // Start monitoring payment status
        startQrPaymentMonitoring(response.data.transactionId, response.data.reference2);

        setMessage({ type: 'success', text: 'QR Code generated! Please scan to pay.' });
      }
    } catch (error) {
      console.error('QR generation error:', error);
      setMessage({ type: 'error', text: 'Failed to generate QR code: ' + (error.response?.data?.error || error.message) });
    } finally {
      setLoading(false);
    }
  };

  // Monitor QR payment status
  const startQrPaymentMonitoring = (transactionId, reference2) => {
    // Clear any existing poll interval and safety-net timeout
    if (qrStatusIntervalRef.current) {
      clearInterval(qrStatusIntervalRef.current);
      qrStatusIntervalRef.current = null;
    }
    if (qrPollingTimeoutRef.current) {
      clearTimeout(qrPollingTimeoutRef.current);
      qrPollingTimeoutRef.current = null;
    }

    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/ldb/query-transaction/${transactionId}?reference2=${reference2}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('QR Payment Status:', response.data);
        //console.log('QR Payment Status:', response.data.paymentRefNo);

        if (response.data.success && response.data.isPaid) {
          clearInterval(qrStatusIntervalRef.current);
          qrStatusIntervalRef.current = null;
          if (qrPollingTimeoutRef.current) {
            clearTimeout(qrPollingTimeoutRef.current);
            qrPollingTimeoutRef.current = null;
          }
          setQrPaymentStatus('paid');
          setMessage({ type: 'success', text: '✅ Payment received! Processing transaction...' });
          const paymentRefNo = response.data.paymentRefNo;
          const paymentBank = response.data.paymentBank;
          // Process the manual payment transaction
          await processQrPayment(transactionId, reference2, paymentRefNo, paymentBank);
        }
      } catch (error) {
        console.error('QR status check error:', error);
      }
    }, 3000); // Check every 3 seconds

    qrStatusIntervalRef.current = interval;

    // Safety-net: stop polling after QR_EXPIRY_MINUTES (countdown timer normally handles this)
    // Stored in a ref so it can be cancelled when a new QR is generated
    qrPollingTimeoutRef.current = setTimeout(() => {
      if (qrStatusIntervalRef.current) {
        clearInterval(qrStatusIntervalRef.current);
        qrStatusIntervalRef.current = null;
      }
      qrPollingTimeoutRef.current = null;
      setQrPaymentStatus(prev => (prev !== 'paid') ? 'expired' : prev);
      console.log(`⏱️ QR polling stopped after ${QR_EXPIRY_MINUTES} minutes`);
    }, QR_EXPIRY_MINUTES * 60 * 1000);
  };

  // Process payment after QR is paid
  const processQrPayment = async (qrTransactionId, reference2, paymentRefNo, paymentBank) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        licensePlate,
        vehicleType: vehicleData?.vehicle_type || 'Unknown',
        tariffId: vehicleData?.tariff?.id || 1,
        amount: parseFloat(amount),
        paymentMethod: 'qr',
        cardId: null,
        deviceType: null,
        gateId,
        operatorNotes: notes + ` | QR TxnID: ${qrTransactionId}`,
        qrTransactionId: qrTransactionId,
        qrReference2: reference2,
        paymentRefNo: paymentRefNo,
        paymentBank: paymentBank,
        skipBarrier: true  // Delay barrier opening until after PDF receipt
      };

      const response = await axios.post(
        `${API_URL}/manual-payment/process`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setLastTransaction(response.data.transaction);
        setShowReceipt(true);
        setMessage({ type: 'success', text: '✅ QR Payment processed successfully!' });

        // Open PDF receipt with authentication
        const receiptId = response.data.transaction.transaction_id || response.data.transaction.id;
        try {
          const pdfResponse = await axios.get(
            `${API_URL}/manual-payment/receipt-pdf/${receiptId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            }
          );

          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);

          console.log('📄 QR Payment PDF receipt opened, waiting 5 seconds before opening barrier...');
        } catch (pdfError) {
          console.error('PDF generation error:', pdfError);
          // Continue to open barrier even if PDF fails
        }

        // Open barrier 5 seconds after PDF receipt is opened
        if (barrierTimeoutRef.current) clearTimeout(barrierTimeoutRef.current);
        barrierTimeoutRef.current = setTimeout(async () => {
          try {
            console.log('🚧 Opening barrier after 5 second delay (QR payment)...');
            await axios.post(
              `${API_URL}/manual-payment/open-barrier`,
              {
                gateId: gateId,
                transactionId: receiptId,
                reason: 'qr_payment_after_receipt'
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('✅ Barrier opened successfully (QR payment)');
            setMessage({ type: 'success', text: '✅ Barrier opened!' });
          } catch (barrierError) {
            console.error('⚠️ Barrier open error:', barrierError);
            setMessage({ type: 'warning', text: 'Payment processed but barrier may not have opened.' });
          }
        }, 5000);

        // Reset after 8 seconds (5 sec barrier delay + 3 sec buffer)
        if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = setTimeout(() => {
          resetForm();
        }, 8000);
      }
    } catch (error) {
      console.error('QR Payment processing error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to process QR payment'
      });
    } finally {
      setLoading(false);
    }
  };

  // Print QR code - optimized for 80mm thermal paper (58mm printable area)
  const printQRCode = () => {
    if (!qrImage) return;

    // Get current date/time for receipt
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
    const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Payment</title>
          <style>
            @page {
              size: 100mm 148mm;
              margin: 0;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            html {
              width: 100mm;
              height: 148mm;
              display: flex;
              justify-content: center;
              align-items: flex-start;
            }
            body { 
              width: 70mm;
              max-width: 70mm;
              margin: 5mm auto 0 auto;
              text-align: left; 
              font-family: Arial, sans-serif; 
              padding: 3mm;
              background: white;
              font-size: 10px;
              line-height: 1.2;
            }
            .header {
              display: flex;
              justify-content: space-between;
              font-size: 8px;
              color: #000;
              margin-bottom: 2mm;
              padding-bottom: 2mm;
              border-bottom: 1px dashed #000;
            }
            .title { 
              font-size: 11px;
              font-weight: bold;
              color: #000;
              margin: 2mm 0;
            }
            .plate {
              font-size: 10px;
              color: #000;
              margin: 1mm 0;
            }
            .amount { 
              font-size: 14px; 
              font-weight: bold; 
              color: #000;
              margin: 2mm 0;
            }
            .qr-container {
              margin: 2mm 0;
              padding: 0;
              background: white;
              text-align: left;
            }
            .qr-container img { 
              width: 40mm;
              height: 40mm;
              max-width: 40mm;
              max-height: 40mm;
              display: block;
              margin: 0;
            }
            .gate {
              font-size: 11px;
              font-weight: bold;
              margin: 2mm 0;
            }
            .footer {
              font-size: 8px;
              color: #000;
              margin-top: 2mm;
              padding-top: 2mm;
              border-top: 1px dashed #000;
            }
            @media print {
              html {
                width: 100mm;
                height: 148mm;
                display: flex;
                justify-content: center;
              }
              body {
                width: 70mm !important;
                max-width: 70mm !important;
                margin: 5mm auto 0 auto !important;
              }
              .qr-container img {
                width: 40mm !important;
                height: 40mm !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <span>${dateStr}, ${timeStr}</span>
            <span>QR Pay</span>
          </div>
          <div class="title">Scan QR to Pay</div>
          <div class="plate">Plate: <b>${licensePlate}</b></div>
          <div class="amount">${formatAmount(amount)} K</div>
          <div class="qr-container">
            <img src="${qrImage}" alt="QR" />
          </div>
          <div class="gate">Gate: ${gateId}</div>
          <div class="footer">
            Scan with LDB App
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  // Cleanup intervals and timeouts on unmount
  useEffect(() => {
    return () => {
      if (qrStatusIntervalRef.current) clearInterval(qrStatusIntervalRef.current);
      if (qrPollingTimeoutRef.current) clearTimeout(qrPollingTimeoutRef.current);
      if (barrierTimeoutRef.current) clearTimeout(barrierTimeoutRef.current);
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      if (qrExpiryTimerRef.current) clearInterval(qrExpiryTimerRef.current);
    };
  }, []);



  // Process payment
  const processPayment = async (e) => {
    e.preventDefault();

    if (!licensePlate || !amount || !gateId) {
      setMessage({ type: 'error', text: getMessage('manual_payment.error_missing_fields') });
      return;
    }

    // Require vehicle type selection (tariff must be set)
    if (!vehicleData?.tariff) {
      setMessage({ type: 'error', text: 'Please select a vehicle type to get the correct tariff amount.' });
      return;
    }

    if (paymentMethod !== 'cash' && paymentMethod !== 'qr' && !cardId) {
      setMessage({ type: 'error', text: getMessage('manual_payment.error_no_card') });
      return;
    }

    // Check if balance is insufficient for card/OBU payment (skip for QR as it's already paid)
    if (paymentMethod !== 'cash' && paymentMethod !== 'qr' && balance !== null && parseFloat(balance) < parseFloat(amount)) {
      setMessage({
        type: 'warning',
        text: `Insufficient balance! Current: ${formatAmount(balance)} ₭, Need: ${formatAmount(amount)} ₭. Please top-up via other channels.`
      });
      return;
    }

    // For QR payment, check if payment is confirmed
    if (paymentMethod === 'qr' && qrPaymentStatus !== 'paid') {
      setMessage({ type: 'warning', text: 'Please wait for QR payment to be confirmed first' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const payload = {
        licensePlate,
        vehicleType: vehicleData?.vehicle_type || 'Unknown',
        tariffId: vehicleData?.tariff?.id || 1,
        amount: parseFloat(amount),
        paymentMethod,
        cardId: paymentMethod !== 'cash' ? cardId : null,
        deviceType: paymentMethod === 'card' ? 'mifare' : 'obu',
        gateId,
        operatorNotes: notes,
        skipBarrier: true  // Delay barrier opening until after PDF receipt
      };

      const response = await axios.post(
        `${API_URL}/manual-payment/process`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setLastTransaction(response.data.transaction);
        setShowReceipt(true);
        setMessage({ type: 'success', text: getMessage('manual_payment.success') });

        // Open PDF receipt with authentication using transaction_id (UUID) or id
        const receiptId = response.data.transaction.transaction_id || response.data.transaction.id;
        try {
          const pdfResponse = await axios.get(
            `${API_URL}/manual-payment/receipt-pdf/${receiptId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            }
          );

          // Create blob URL and open in new tab
          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');

          // Clean up blob URL after a delay
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);

          console.log('📄 PDF receipt opened, waiting 5 seconds before opening barrier...');
        } catch (pdfError) {
          console.error('PDF generation error:', pdfError);
          // Continue to open barrier even if PDF fails
        }

        // Open barrier 5 seconds after PDF receipt is opened
        if (barrierTimeoutRef.current) clearTimeout(barrierTimeoutRef.current);
        barrierTimeoutRef.current = setTimeout(async () => {
          try {
            console.log('🚧 Opening barrier after 5 second delay...');
            await axios.post(
              `${API_URL}/manual-payment/open-barrier`,
              {
                gateId: gateId,
                transactionId: receiptId,
                reason: 'manual_payment_after_receipt'
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('✅ Barrier opened successfully');
            setMessage({ type: 'success', text: '✅ Barrier opened!' });
          } catch (barrierError) {
            console.error('⚠️ Barrier open error:', barrierError);
            setMessage({ type: 'warning', text: 'Payment processed but barrier may not have opened.' });
          }
        }, 5000);

        // Reset after 8 seconds (5 sec barrier delay + 3 sec buffer)
        if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = setTimeout(() => {
          resetForm();
        }, 8000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || getMessage('manual_payment.error_process')
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setLicensePlate('');
    setVehicleData(null);
    setAmount('');
    setPaymentMethod('qr');
    setPaymentMethodLocked(false);
    setCardId('');
    setBalance(null);
    setNotes('');
    setMessage({ type: '', text: '' });
    setShowReceipt(false);

    // Clear vehicle selection states
    setVehicleOptions([]);
    setShowVehicleSelection(false);
    setSelectedVehicleType('');
    setShowVehicleTypeSelection(false);

    // Clear QR states
    setQrImage(null);
    setQrTransactionData(null);
    setQrPaymentStatus(null);
    setQrExpiresAt(null);
    setQrTimeLeft(null);
    if (qrStatusIntervalRef.current) {
      clearInterval(qrStatusIntervalRef.current);
      qrStatusIntervalRef.current = null;
    }
    if (qrPollingTimeoutRef.current) {
      clearTimeout(qrPollingTimeoutRef.current);
      qrPollingTimeoutRef.current = null;
    }
    if (qrExpiryTimerRef.current) {
      clearInterval(qrExpiryTimerRef.current);
      qrExpiryTimerRef.current = null;
    }
    if (barrierTimeoutRef.current) {
      clearTimeout(barrierTimeoutRef.current);
      barrierTimeoutRef.current = null;
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-t-xl shadow-lg p-6 border-b-4 border-blue-500">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
            <span className="text-4xl">🚧</span>
            {getMessage('manual_payment.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            {getMessage('manual_payment.description')}
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-b-xl shadow-lg p-6">

          {/* Alert Messages */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg border-l-4 ${message.type === 'success' ? 'bg-green-50 border-green-500 text-green-800 dark:bg-green-900 dark:text-green-200' :
              message.type === 'error' ? 'bg-red-50 border-red-500 text-red-800 dark:bg-red-900 dark:text-red-200' :
                message.type === 'warning' ? 'bg-yellow-50 border-yellow-500 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                  'bg-blue-50 border-blue-500 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              }`}>
              {message.text}
            </div>
          )}



          {/* Receipt Modal */}
          {showReceipt && lastTransaction && (
            <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900 dark:to-emerald-900 border-2 border-green-400 rounded-xl p-6 shadow-xl">
              <h2 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-4 text-center">
                🎉 {getMessage('manual_payment.success')}
              </h2>

              {/* Show topup details if available */}
              {lastTransaction.topupAmount && (
                <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg border-2 border-yellow-400">
                  <h3 className="font-bold text-yellow-800 dark:text-yellow-200 mb-2 flex items-center gap-2">
                    <span>💵</span> Cash Top-up Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Topup Transaction ID:</span>
                      <p className="font-bold text-gray-900 dark:text-white font-mono text-xs break-all">
                        {lastTransaction.topup_transaction_id}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Topup Amount:</span>
                      <p className="font-bold text-yellow-700 dark:text-yellow-300">
                        {formatAmount(lastTransaction.topupAmount)} ₭
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Balance Before:</span>
                      <p className="font-bold text-gray-900 dark:text-white">
                        {formatAmount(lastTransaction.balanceBeforeTopup)} ₭
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Balance After Topup:</span>
                      <p className="font-bold text-green-600 dark:text-green-400">
                        {formatAmount(lastTransaction.balanceAfterTopup)} ₭
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Toll payment details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="col-span-2">
                  <span className="text-gray-600 dark:text-gray-400">Toll Transaction ID:</span>
                  <p className="font-bold text-gray-900 dark:text-white font-mono text-xs break-all">
                    {lastTransaction.transaction_id || lastTransaction.id}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{getMessage('manual_payment.license_plate')}:</span>
                  <p className="font-bold text-gray-900 dark:text-white">{lastTransaction.licensePlate}</p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Toll Amount:</span>
                  <p className="font-bold text-red-600 dark:text-red-400">
                    {formatAmount(lastTransaction.tollAmount || lastTransaction.amount)} ₭
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{getMessage('manual_payment.payment_method_label')}:</span>
                  <p className="font-bold text-gray-900 dark:text-white uppercase">{lastTransaction.paymentMethod}</p>
                </div>
                {lastTransaction.balanceAfterToll !== undefined && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Final Balance:</span>
                    <p className="font-bold text-blue-600 dark:text-blue-400">
                      {formatAmount(lastTransaction.balanceAfterToll)} ₭
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <form onSubmit={processPayment}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* LEFT: Vehicle Search & Info */}
              <div className="lg:col-span-2 space-y-6">

                {/* License Plate Search */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 rounded-lg p-6 shadow-md">
                  <label className="block text-lg font-bold text-gray-800 dark:text-white mb-3">
                    🚗 {getMessage('manual_payment.license_plate')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={licensePlate}
                      onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                      className="w-full text-3xl font-bold text-center p-4 border-4 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-200 dark:bg-gray-700 dark:text-white dark:border-blue-600 uppercase tracking-wider"
                      placeholder={getMessage('manual_payment.license_plate_placeholder')}
                      required
                      autoFocus
                    />
                    {searching && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
                    Auto-search activated • Type at least 3 characters
                  </p>
                </div>

                {/* Vehicle Selection List (for duplicate license plates) */}
                {showVehicleSelection && vehicleOptions.length > 0 && (
                  <div className="mb-6">
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-300 dark:border-yellow-700 rounded-lg p-3 mb-4">
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                        ⚠️ Found {vehicleOptions.length} vehicle(s) with this plate. Please select the correct one:
                      </p>
                    </div>

                    <div className="bg-white dark:bg-gray-700 border-2 border-blue-400 dark:border-blue-500 rounded-lg shadow-2xl max-h-96 overflow-y-auto">
                      {vehicleOptions.map((vehicle, index) => (
                        <div
                          key={index}
                          onClick={() => handleVehicleSelect(vehicle)}
                          className="p-4 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer border-b-2 border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors"
                        >
                          <div className="space-y-2">
                            {/* Header: License Plate and Vehicle Type */}
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                    🚗 {vehicle.license_plate}
                                  </span>
                                </div>
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {vehicle.vehicle_type}
                                </div>
                                {/* Province and Brand - Modern colors */}
                                <div className="flex items-center gap-3 flex-wrap">
                                  {vehicle.owner_province && (
                                    <span className="text-base font-bold text-purple-600 dark:text-purple-400">
                                      📍 ແຂວງ: {vehicle.owner_province}
                                    </span>
                                  )}
                                  {(vehicle.vehicle_model || vehicle.model) && (
                                    <span className="text-base font-bold text-orange-600 dark:text-orange-400">
                                      🚙 ສີ: {vehicle.vehicle_model || vehicle.model}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {/* Owner */}
                              {vehicle.owner_name && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">👤 Owner:</span>
                                  <span className="ml-1 font-medium text-gray-900 dark:text-white">{vehicle.owner_name}</span>
                                </div>
                              )}

                              {/* Color */}
                              {(vehicle.vehicle_color || vehicle.color) && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">🎨 Color:</span>
                                  <span className="ml-1 font-medium text-gray-900 dark:text-white">{vehicle.vehicle_color || vehicle.color}</span>
                                </div>
                              )}
                            </div>

                            {/* Devices */}
                            <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Devices:</div>
                              <div className="flex gap-2 flex-wrap">
                                {vehicle.mifare_card && (
                                  <span className="text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                                    💳 {vehicle.mifare_card}
                                  </span>
                                )}
                                {vehicle.obu_device_id && (
                                  <span className="text-xs px-3 py-1 rounded-full font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
                                    📡 {vehicle.obu_device_id}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vehicle Type Selection (when vehicle not found OR vehicle found but no tariff) */}
                {((showVehicleTypeSelection && !vehicleData) || (vehicleData && !vehicleData.tariff)) && licensePlate.length >= 3 && (
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900 dark:to-amber-900 rounded-lg p-6 shadow-md border-2 border-orange-300 dark:border-orange-600">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">⚠️</span>
                      <h3 className="text-lg font-bold text-orange-800 dark:text-orange-200">
                        {vehicleData ? 'Vehicle Found - No Tariff Set' : 'Vehicle Not Found'} - Select Vehicle Type
                      </h3>
                    </div>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mb-4">
                      {vehicleData
                        ? `Vehicle "${vehicleData.license_plate}" found but has no tariff. Please select vehicle type:`
                        : 'Please select the vehicle type to get the correct tariff amount:'
                      }
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {tariffs.map((tariff, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleVehicleTypeSelect(tariff.vehicle_type)}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${selectedVehicleType === tariff.vehicle_type
                            ? 'bg-green-100 border-green-500 dark:bg-green-900 dark:border-green-400 ring-2 ring-400'
                            : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30'
                            }`}
                        >
                          <div className="text-2xl mb-1">🚗</div>
                          <div className="font-bold text-gray-800 dark:text-white text-sm">
                            {tariff.vehicle_type}
                          </div>
                          <div className="text-lg font-bold text-green-600 dark:text-green-400 mt-1">
                            {formatAmount(tariff.kip_rate)} ₭
                          </div>
                        </button>
                      ))}
                    </div>

                    {!selectedVehicleType && (
                      <p className="mt-4 text-sm text-center text-red-600 dark:text-red-400">
                        ⚠️ Please select a vehicle type before proceeding
                      </p>
                    )}
                  </div>
                )}

                {/* Vehicle Information Card */}
                {vehicleData && (
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900 dark:to-teal-900 rounded-lg p-6 shadow-md border-2 border-emerald-300 dark:border-emerald-600">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <span className="text-2xl">✅</span> Vehicle Found
                      </h3>
                      {vehicleData.customer_type && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${vehicleData.customer_type === 'VIP'
                          ? 'bg-yellow-400 text-yellow-900 border-yellow-600'
                          : 'bg-blue-100 text-blue-900 border-blue-400 dark:bg-blue-900 dark:text-blue-100'
                          }`}>
                          {vehicleData.customer_type === 'VIP' ? '⭐ VIP' : '👤 Normal'}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Type:</span>
                        <p className="font-bold text-gray-900 dark:text-white truncate" title={vehicleData.vehicle_type}>
                          {vehicleData.vehicle_type || 'Unknown'}
                        </p>
                      </div>
                      {vehicleData.make && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Brand:</span>
                          <p className="font-bold text-gray-900 dark:text-white">{vehicleData.make}</p>
                        </div>
                      )}
                      {(vehicleData.model || vehicleData.vehicle_model) && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Model:</span>
                          <p className="font-bold text-gray-900 dark:text-white">{vehicleData.model || vehicleData.vehicle_model}</p>
                        </div>
                      )}
                      {(vehicleData.color || vehicleData.vehicle_color) && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Color:</span>
                          <p className="font-bold text-gray-900 dark:text-white flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full border border-gray-400" style={{ backgroundColor: (vehicleData.color || vehicleData.vehicle_color || '').toLowerCase() }}></span>
                            {vehicleData.color || vehicleData.vehicle_color}
                          </p>
                        </div>
                      )}
                      {vehicleData.owner_name && (
                        <div className="col-span-2">
                          <span className="text-gray-600 dark:text-gray-400">Owner:</span>
                          <p className="font-bold text-gray-900 dark:text-white">{vehicleData.owner_name}</p>
                        </div>
                      )}
                      {vehicleData.owner_phone && (
                        <div className="col-span-2">
                          <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                          <p className="font-bold text-blue-600 dark:text-blue-400">📞 {vehicleData.owner_phone}</p>
                        </div>
                      )}
                    </div>

                    {vehicleData.tariff && (
                      <div className="mt-4 pt-4 border-t-2 border-emerald-300 dark:border-emerald-600">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700 dark:text-gray-300 font-semibold">Suggested Tariff:</span>
                          <span className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                            {formatAmount(vehicleData.tariff.kip_rate)} ₭
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{vehicleData.tariff.name}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* QR Code Display - Moved below Notes */}
                {paymentMethod === 'qr' && (
                  <div className="mt-4">
                    {!qrImage && !loading && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-300 dark:border-yellow-700 rounded-lg p-4 text-center">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          ℹ️ Click "Generate QR" or the QR Payment option will auto-generate when amount and license plate are set
                        </p>
                        <button
                          type="button"
                          onClick={generateQRCode}
                          disabled={!amount || !licensePlate || loading}
                          className="mt-3 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          📱 Generate QR Code
                        </button>
                      </div>
                    )}

                    {qrImage && (
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900 dark:to-purple-900 rounded-lg p-6 border-2 border-indigo-300 dark:border-indigo-600">
                        <h3 className="text-lg font-bold text-center mb-4 text-indigo-700 dark:text-indigo-300">
                          📱 Scan QR Code to Pay
                        </h3>

                        {/* QR Image with expired overlay + countdown badge */}
                        <div className="flex justify-center mb-4">
                          <div className="relative inline-block bg-white p-3 rounded-xl shadow-md">
                            <img
                              src={qrImage}
                              alt="QR Code"
                              className={`w-40 h-40 transition-all duration-300 ${qrPaymentStatus === 'expired' ? 'opacity-20 blur-sm' : ''
                                }`}
                            />
                            {qrPaymentStatus === 'expired' && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 dark:bg-gray-900/80 rounded-xl">
                                <span className="text-4xl mb-1">⛔</span>
                                <p className="text-red-600 dark:text-red-400 font-bold text-sm">QR Expired</p>
                                <p className="text-gray-500 text-xs">ກະລຸນາສ້າງ QR ໃໝ່</p>
                              </div>
                            )}
                            {/* Countdown badge */}
                            {qrPaymentStatus !== 'expired' && qrPaymentStatus !== 'paid' && qrTimeLeft !== null && (
                              <div className={`absolute -top-4 -right-4 px-3 py-1 rounded-full text-sm font-bold shadow-lg z-10 flex items-center gap-1 ${qrTimeLeft <= 60
                                ? 'bg-red-500 text-white animate-pulse'
                                : qrTimeLeft <= 180
                                  ? 'bg-orange-400 text-white'
                                  : 'bg-[#10B981] text-white'
                                }`}>
                                ⏱️ {Math.floor(qrTimeLeft / 60)}:{String(qrTimeLeft % 60).padStart(2, '0')}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-center space-y-2">
                          <p className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">
                            {formatAmount(amount)} ₭
                          </p>

                          {qrPaymentStatus === 'pending' && (
                            <div className="bg-yellow-100 dark:bg-yellow-900/50 rounded-lg p-3 mt-3">
                              <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center justify-center gap-2">
                                <span className="animate-pulse text-xl">⏳</span>
                                <span className="font-semibold">Waiting for payment...</span>
                              </p>
                              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                Checking payment status every 3 seconds
                              </p>
                            </div>
                          )}

                          {qrPaymentStatus === 'paid' && (
                            <div className="bg-green-100 dark:bg-green-900/50 rounded-lg p-3 mt-3">
                              <p className="text-sm text-green-800 dark:text-green-200 flex items-center justify-center gap-2">
                                <span className="text-xl">✅</span>
                                <span className="font-semibold">Payment received! Processing...</span>
                              </p>
                            </div>
                          )}

                          {qrPaymentStatus === 'expired' && (
                            <div className="bg-red-50 dark:bg-red-900/40 border border-red-300 dark:border-red-700 rounded-lg p-3 mt-3">
                              <p className="text-sm text-red-700 dark:text-red-300 font-bold flex items-center justify-center gap-2">
                                <span className="text-xl">⛔</span>
                                QR Code ໝົດອາຍຸແລ້ວ
                              </p>
                              <p className="text-xs text-red-500 dark:text-red-400 mt-1 text-center">
                                Please generate a new QR code.
                              </p>
                            </div>
                          )}

                          <div className="flex gap-3 justify-center mt-4">
                            <button
                              type="button"
                              onClick={printQRCode}
                              disabled={qrPaymentStatus === 'expired'}
                              className={`px-6 py-2 rounded-lg transition flex items-center gap-2 ${qrPaymentStatus === 'expired'
                                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed opacity-60'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                }`}
                            >
                              🖨️ Print QR Code
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setQrImage(null);
                                setQrTransactionData(null);
                                setQrPaymentStatus(null);
                                setQrExpiresAt(null);
                                setQrTimeLeft(null);
                                if (qrStatusIntervalRef.current) {
                                  clearInterval(qrStatusIntervalRef.current);
                                  qrStatusIntervalRef.current = null;
                                }
                                if (qrExpiryTimerRef.current) {
                                  clearInterval(qrExpiryTimerRef.current);
                                  qrExpiryTimerRef.current = null;
                                }
                              }}
                              className={`px-6 py-2 rounded-lg text-white transition ${qrPaymentStatus === 'expired'
                                ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                                : 'bg-gray-500 hover:bg-gray-600'
                                }`}
                            >
                              {qrPaymentStatus === 'expired' ? '🔄 Generate New QR' : '🔄 Regenerate'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RIGHT: Payment Panel */}
              <div className="space-y-6">

                {/* Amount */}
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900 dark:to-amber-900 rounded-lg p-6 shadow-md border-2 border-yellow-300 dark:border-yellow-600">
                  <label className="block text-sm font-bold text-gray-800 dark:text-white mb-2">
                    💰 {getMessage('manual_payment.amount_label')} <span className="text-red-500">*</span>
                    {vehicleData?.tariff && (
                      <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">✓ Auto-filled from tariff</span>
                    )}
                  </label>
                  {vehicleData?.tariff ? (
                    // Read-only formatted display when tariff is set
                    <div
                      className="w-full text-4xl font-bold text-center p-4 border-4 rounded-lg dark:text-white border-green-400 bg-green-50 dark:bg-green-900 cursor-not-allowed"
                      title="Amount is auto-filled from tariff and cannot be edited"
                    >
                      {formatAmount(amount)}
                    </div>
                  ) : (
                    // Prompt to select vehicle type when no tariff
                    <div
                      className="w-full text-xl font-bold text-center p-4 border-4 rounded-lg border-red-400 bg-red-50 dark:bg-red-900 dark:text-white cursor-not-allowed"
                      title="Please select vehicle type to get the correct amount"
                    >
                      {amount ? formatAmount(amount) : '⚠️ Select Vehicle Type'}
                    </div>
                  )}
                  {vehicleData?.tariff ? (
                    <p className="mt-2 text-xs text-center text-gray-600 dark:text-gray-400">
                      🔒 Amount locked from tariff: {vehicleData.tariff.name}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-center text-red-600 dark:text-red-400">
                      ⚠️ Please search vehicle or select vehicle type to get correct amount
                    </p>
                  )}
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                    {getMessage('manual_payment.payment_method_label')} <span className="text-red-500">*</span>
                  </label>

                  {/* Lock notice when no device mapped */}
                  {paymentMethodLocked && (
                    <div className="mb-3 p-3 bg-orange-50 dark:bg-orange-900/40 border-2 border-orange-400 dark:border-orange-600 rounded-lg flex items-start gap-2">
                      <span className="text-lg">🔒</span>
                      <div>
                        <p className="text-xs font-bold text-orange-800 dark:text-orange-200">
                          ไม่พบ Mifare Card / OBU Device ที่ผูกกับทะเบียนนี้
                        </p>
                        <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5">
                          กำหนดการชำระเป็น QR LDB เท่านั้น — ปุ่ม UHF Card และ OBU ถูกล็อก
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    {/*
                    <label className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'cash'
                      ? 'bg-green-100 border-green-500 dark:bg-green-900 dark:border-green-400'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'
                      }`}>
                      <input
                        type="radio"
                        value="cash"
                        checked={paymentMethod === 'cash'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-xl">💵</span>
                      <span className="font-semibold text-sm text-gray-800 dark:text-white">{getMessage('manual_payment.cash')}</span>
                    </label>
                    */}
                    <label className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'qr'
                      ? 'bg-indigo-100 border-indigo-500 dark:bg-indigo-900 dark:border-indigo-400'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'
                      }`}>
                      <input
                        type="radio"
                        value="qr"
                        checked={paymentMethod === 'qr'}
                        onChange={(e) => {
                          setPaymentMethod(e.target.value);
                          // Auto-generate QR when selected if amount is set
                          if (amount && parseFloat(amount) > 0 && licensePlate) {
                            setTimeout(() => generateQRCode(), 100);
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-xl">📱</span>
                      <span className="font-semibold text-sm text-gray-800 dark:text-white">QR (LDB)</span>
                    </label>

                    <label className={`flex items-center gap-2 p-2 rounded-lg border-2 transition ${paymentMethodLocked
                      ? 'bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700 opacity-40 cursor-not-allowed'
                      : paymentMethod === 'card'
                        ? 'bg-blue-100 border-blue-500 dark:bg-blue-900 dark:border-blue-400 cursor-pointer'
                        : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 cursor-pointer'
                      }`}>
                      <input
                        type="radio"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        disabled={paymentMethodLocked}
                        className="w-4 h-4"
                      />
                      <span className="text-xl">💳</span>
                      <span className="font-semibold text-sm text-gray-800 dark:text-white">{getMessage('manual_payment.uhf_card')}</span>
                      {paymentMethodLocked && <span className="ml-auto text-xs text-gray-400">🔒</span>}
                    </label>

                    <label className={`flex items-center gap-2 p-2 rounded-lg border-2 transition ${paymentMethodLocked
                      ? 'bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700 opacity-40 cursor-not-allowed'
                      : paymentMethod === 'obu'
                        ? 'bg-purple-100 border-purple-500 dark:bg-purple-900 dark:border-purple-400 cursor-pointer'
                        : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 cursor-pointer'
                      }`}>
                      <input
                        type="radio"
                        value="obu"
                        checked={paymentMethod === 'obu'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        disabled={paymentMethodLocked}
                        className="w-4 h-4"
                      />
                      <span className="text-xl">📡</span>
                      <span className="font-semibold text-sm text-gray-800 dark:text-white">{getMessage('manual_payment.obu')}</span>
                      {paymentMethodLocked && <span className="ml-auto text-xs text-gray-400">🔒</span>}
                    </label>
                  </div>
                </div>

                {/* Card/OBU ID - Only show for card or OBU payment methods, not for cash or QR */}
                {(paymentMethod === 'card' || paymentMethod === 'obu') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {paymentMethod === 'card' ? `💳 ${getMessage('manual_payment.card_uid')}` : `📡 ${getMessage('manual_payment.obu_device_id')}`} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={cardId}
                      onChange={(e) => setCardId(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono"
                      placeholder={getMessage('manual_payment.card_id_placeholder')}
                      required
                    />
                    {balance !== null && (
                      <p className={`mt-2 text-sm font-bold ${parseFloat(balance) >= parseFloat(amount) ? 'text-green-600' : 'text-red-600'}`}>
                        {getMessage('manual_payment.current_balance')}: {formatAmount(balance)} ₭
                        {parseFloat(balance) < parseFloat(amount) && ` (${getMessage('manual_payment.insufficient')})`}
                      </p>
                    )}
                  </div>
                )}

                {/* Gate ID - Auto-detected and Locked */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    🚺 {getMessage('manual_payment.gate_id')} <span className="text-red-500">*</span>
                  </label>
                  {loadingBarriers ? (
                    <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading authorized gate...</span>
                    </div>
                  ) : barriers.length > 0 ? (
                    <>
                      {/* Show authorized gate - READ ONLY, user cannot change */}
                      <div className="w-full p-4 border-2 border-green-400 rounded-lg bg-green-50 dark:bg-green-900 dark:border-green-600">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-lg font-bold text-gray-900 dark:text-white uppercase">
                              {barriers[0].name}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {barriers[0].barrier_code} - {barriers[0].barrier_type.toUpperCase()}
                            </p>
                            {barriers[0].location && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                📍 {barriers[0].location}
                              </p>
                            )}
                          </div>
                          <div className="ml-4">
                            <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                              🔒 LOCKED
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Hidden input to store the actual gate code */}
                      <input type="hidden" value={gateId} />
                      <p className="mt-2 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                        <span>✓</span>
                        <span>This gate is authorized for your IP address. You cannot select other gates for security reasons.</span>
                      </p>
                    </>
                  ) : (
                    <div className="w-full p-4 border-2 border-red-400 rounded-lg bg-red-50 dark:bg-red-900">
                      <p className="text-red-800 dark:text-red-200 font-bold">⚠️ No authorized gate found</p>
                      <p className="text-sm text-red-600 dark:text-red-300 mt-1">Your IP is not registered for any gate. Contact administrator.</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-4">
                  {/* QR payment info message when waiting */}
                  {paymentMethod === 'qr' && qrPaymentStatus !== 'paid' && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-400 rounded-lg text-center">
                      <p className="text-sm font-bold text-yellow-800 dark:text-yellow-200">
                        📱 {qrPaymentStatus === 'pending'
                          ? 'Waiting for QR payment confirmation...'
                          : 'Please generate and scan QR Code to pay first'}
                      </p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                        The button will be enabled automatically after payment is confirmed
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={
                      loading ||
                      showReceipt ||
                      (paymentMethod === 'qr' && qrPaymentStatus !== 'paid') ||
                      (paymentMethodLocked && qrPaymentStatus !== 'paid')
                    }
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        {getMessage('manual_payment.processing')}
                      </span>
                    ) : showReceipt ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="text-2xl">✅</span>
                        Success
                      </span>
                    ) : paymentMethod === 'qr' && qrPaymentStatus !== 'paid' ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="text-2xl">⏳</span>
                        Waiting for QR Payment...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <span className="text-2xl">✅</span>
                        {getMessage('manual_payment.process_btn')}
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={resetForm}
                    className="w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg shadow transition"
                  >
                    🔄 {getMessage('manual_payment.reset_btn')}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ManualPayment;
